import { useEffect, useState } from "react";
import { api, inr, fmtDate, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { Search, PackagePlus, Trash2, PackageCheck, TrendingUp, Pencil, ChevronsUpDown } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from "recharts";

const th = "text-left text-[10px] uppercase tracking-widest text-slate-400 font-semibold px-3 py-2.5";
const td = "px-3 py-2.5 text-[13px] text-slate-700";

const MarginBadge = ({ v }) => v == null ? <span className="text-slate-300">—</span> : (
  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${v >= 25 ? "bg-emerald-50 text-emerald-700" : v >= 12 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-600"}`}>{v}%</span>
);

function SizeFinder() {
  const [sizeInput, setSizeInput] = useState("");
  const [category, setCategory] = useState("all");
  const [cats, setCats] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/products").then((r) => setCats([...new Set(r.data.map((p) => p.category).filter(Boolean))])).catch(() => {});
  }, []);
  const pair = parsePair(sizeInput);
  const search = async () => {
    if (!pair) return;
    setBusy(true);
    try {
      const catQ = category !== "all" ? `&category=${encodeURIComponent(category)}` : "";
      const r = await api.get(`/size-intel/search?id_mm=${pair.id}&od_mm=${pair.od}${catQ}`);
      setRes(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200 rounded-xl shadow-none flex flex-wrap items-start gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Size (ID x OD)</Label>
          <Input value={sizeInput} onChange={(e) => setSizeInput(e.target.value)} className="mt-1.5 w-40" placeholder="e.g. 4 x 7" data-testid="si-size" />
          <p className="text-[11px] text-blue-700 mt-1 h-4">{pair ? `ID ${pair.id}mm x OD ${pair.od}mm` : ""}</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="mt-1.5 w-48 h-10" data-testid="si-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={search} disabled={busy || !pair} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white mt-[26px]" data-testid="si-search">
          <Search size={14} className="mr-1.5" /> {busy ? "Searching…" : "Search History"}
        </Button>
        {res && <span className="text-xs text-slate-400 mt-[34px]">{res.rows.length} lead record{res.rows.length === 1 ? "" : "s"}</span>}
      </Card>

      {res?.stock?.length > 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex flex-wrap items-center gap-3" data-testid="si-stock-alert">
          <PackageCheck size={18} className="text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-800">In stock now:</span>
          {res.stock.map((s) => (
            <span key={s.id} className="text-xs bg-white border border-emerald-200 rounded-full px-3 py-1 text-emerald-700">
              {s.product_name} {s.size} {s.color && `· ${s.color}`} · {s.quantity} {s.unit}</span>
          ))}
        </div>
      )}

      {res && (
        <Card className="border-slate-200 rounded-xl shadow-none overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr><th className={th}>Product</th><th className={th}>Size</th><th className={th}>Customer</th><th className={th}>Date</th>
                  <th className={`${th} text-right`}>Qty</th><th className={`${th} text-right`}>Purchase (₹)</th>
                  <th className={th}>Margin %</th><th className={`${th} text-right`}>Sales Rate</th><th className={th}>Ref</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-50" data-testid="si-results">
              {res.rows.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50/60">
                  <td className={`${td} font-medium`}>{r.product}{r.color && <span className="text-slate-400"> · {r.color}</span>}</td>
                  <td className={td}>{r.size}</td>
                  <td className={td}>{r.customer}</td>
                  <td className={td}>{fmtDate(r.date)}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.quantity} {r.unit}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.purchase_rate ? `₹${r.purchase_rate}` : "—"}</td>
                  <td className={td}><MarginBadge v={r.margin_pct} /></td>
                  <td className={`${td} text-right tabular-nums font-semibold`}>₹{r.sales_rate}</td>
                  <td className={td}><span className="text-[10px] uppercase tracking-wider bg-slate-100 rounded px-1.5 py-0.5 text-slate-500">{r.source}</span> <span className="text-xs text-slate-400">{r.number}</span></td>
                </tr>
              ))}
              {res.rows.length === 0 && <tr><td colSpan={9} className="text-center text-sm text-slate-400 py-10">No history found for this size.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

const EMPTY_STOCK = { product_name: "", size_input: "", color: "", quantity: "", unit: "Meter" };

const parsePair = (s) => {
  const m = (s || "").match(/([\d.]+)\s*[xX*×]\s*([\d.]+)/);
  if (!m) return null;
  const a = +m[1], b = +m[2];
  return { id: Math.min(a, b), od: Math.max(a, b) };
};

const fmtStockSize = (s) => {
  const m = /ID\s*([\d.]+)\s*(?:mm)?\s*[x×*]\s*OD\s*([\d.]+)/i.exec(s || "") || /([\d.]+)\s*(?:mm)?\s*[x×*]\s*([\d.]+)/.exec(s || "");
  if (!m) return s || "—";
  const idv = Math.min(+m[1], +m[2]), odv = Math.max(+m[1], +m[2]);
  const wall = ((odv - idv) / 2).toFixed(2).replace(/\.?0+$/, "");
  return `ID ${idv}mm × OD ${odv}mm × Wall ${wall}mm`;
};

function ProductCombobox({ products, value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox"
                className="mt-1 w-full h-9 justify-between font-normal text-left" data-testid="stock-product">
          <span className={`truncate ${value ? "text-slate-800" : "text-slate-400"}`}>{value || "Search product…"}</span>
          <ChevronsUpDown size={13} className="opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[340px]" align="start">
        <Command>
          <CommandInput placeholder="Type to search products…" data-testid="stock-product-search" />
          <CommandList>
            <CommandEmpty>No product found.</CommandEmpty>
            {products.map((p) => (
              <CommandItem key={p.id} value={`${p.description || ""} ${p.product_code || ""}`}
                           onSelect={() => { onChange(p.description || p.product_code); setOpen(false); }}
                           data-testid={`stock-product-opt-${p.id}`}>
                <span className="truncate">{p.description || p.product_code}</span>
                <span className="ml-auto pl-2 text-[10px] text-slate-400 font-mono shrink-0">{p.product_code}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function StockTab() {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_STOCK });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/size-intel/stock").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const pair = parsePair(form.size_input);
  const wall = pair && pair.od > pair.id ? ((pair.od - pair.id) / 2).toFixed(2).replace(/\.?0+$/, "") : "";
  const sizeLabel = pair ? `ID ${pair.id}mm x OD ${pair.od}mm x Wall ${wall || 0}mm` : "";
  const buildSize = () => sizeLabel || (editing?.size || "");
  const startEdit = (s) => {
    const m = (s.size || "").match(/ID\s*([\d.]+)\s*mm\s*x\s*OD\s*([\d.]+)/i) || (s.size || "").match(/([\d.]+)\s*[xX*×]\s*([\d.]+)/);
    setForm({ product_name: s.product_name, size_input: m ? `${+m[1]} x ${+m[2]}` : "", color: s.color || "", quantity: s.quantity, unit: s.unit || "Meter" });
    setEditing(s);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setForm({ ...EMPTY_STOCK }); };
  const add = async () => {
    try {
      const payload = { product_name: form.product_name, size: buildSize(), color: form.color, unit: form.unit || "Meter", quantity: +form.quantity || 0 };
      if (editing) {
        await api.patch(`/size-intel/stock/${editing.id}`, payload);
        toast.success("Stock updated");
      } else {
        await api.post("/size-intel/stock", payload);
        toast.success("Stock added");
      }
      cancelEdit(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setQty = async (s, q) => {
    try { await api.patch(`/size-intel/stock/${s.id}`, { ...s, quantity: +q || 0 }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Remove ${s.product_name} ${s.size} from stock?`)) return;
    await api.delete(`/size-intel/stock/${s.id}`); load();
  };
  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200 rounded-xl shadow-none">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-2"><PackagePlus size={14} className="text-[#FC5F5F]" /> {editing ? `Edit Stock — ${editing.product_name}` : "Add Finished Goods"}</div>
        <div className="grid sm:grid-cols-6 gap-3 items-start">
          <div className="sm:col-span-2"><Label className="text-[10px] uppercase tracking-widest text-slate-500">Product</Label>
            <ProductCombobox products={products} value={form.product_name} onChange={(v) => setForm({ ...form, product_name: v })} /></div>
          <div className="sm:col-span-2"><Label className="text-[10px] uppercase tracking-widest text-slate-500">Size (ID x OD)</Label>
            <Input value={form.size_input} onChange={(e) => setForm({ ...form, size_input: e.target.value })} className="mt-1 h-9" placeholder="e.g. 8 x 12" data-testid="stock-size" />
            <p className="text-[11px] text-blue-700 mt-1 h-4" data-testid="stock-size-label">{sizeLabel}</p></div>
          <div><Label className="text-[10px] uppercase tracking-widest text-slate-500">Color</Label>
            <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-1 h-9" placeholder="Translucent" data-testid="stock-color" /></div>
          <div className="flex gap-2 items-start">
            <div className="flex-1"><Label className="text-[10px] uppercase tracking-widest text-slate-500">Qty</Label>
              <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1 h-9" data-testid="stock-qty" /></div>
            <Button onClick={add} disabled={!form.product_name || (!pair && !editing)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white mt-[22px] h-9" data-testid="stock-add">{editing ? "Save" : "Add"}</Button>
            {editing && <Button variant="outline" onClick={cancelEdit} className="mt-[22px] h-9" data-testid="stock-cancel">Cancel</Button>}
          </div>
        </div>
      </Card>
      <Card className="border-slate-200 rounded-xl shadow-none overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr><th className={th}>Product</th><th className={th}>Size</th><th className={th}>Color</th><th className={th}>Quantity</th><th className={th}>Updated</th><th className={th}></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-50" data-testid="stock-table">
            {list.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50/60">
                <td className={`${td} font-medium`}>{s.product_name}</td>
                <td className={td}>{fmtStockSize(s.size)}</td>
                <td className={td}>{s.color}</td>
                <td className={td}>
                  <span className="inline-flex items-center gap-1.5">
                    <Input type="number" key={`${s.id}-${s.quantity}`} defaultValue={s.quantity} onBlur={(e) => +e.target.value !== s.quantity && setQty(s, e.target.value)}
                           className="w-24 h-8 text-xs" data-testid={`stock-qty-${s.id}`} /> <span className="text-xs text-slate-400">{s.unit}</span>
                  </span>
                </td>
                <td className={`${td} text-xs text-slate-400`}>{fmtDate(s.updated_at)}</td>
                <td className={td}>
                  <button onClick={() => startEdit(s)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Edit stock" data-testid={`stock-edit-${s.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(s)} className="p-1.5 text-slate-400 hover:text-rose-600" title="Delete" data-testid={`stock-del-${s.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-10">No finished goods stock yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-slate-400">When a new lead is created with a matching size & colour, the team is notified that the product is already in stock.</p>
    </div>
  );
}

function TrendsTab() {
  const [customers, setCustomers] = useState([]);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);
  useEffect(() => { if (cid) api.get(`/size-intel/trends/${cid}`).then((r) => setData(r.data)); }, [cid]);
  const s = data?.summary;
  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200 rounded-xl shadow-none flex flex-wrap items-center gap-4">
        <TrendingUp size={16} className="text-[#FC5F5F]" />
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger className="w-72" data-testid="trend-customer"><SelectValue placeholder="Select a customer…" /></SelectTrigger>
          <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
        </Select>
        {data && <span className="text-xs text-slate-400">Based on {data.based_on}</span>}
      </Card>
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="trend-summary">
            {[["Total Business", inr(s.total_value)], ["Orders", s.order_count], ["Avg Order Value", inr(s.avg_order_value)],
              ["Last Purchase", s.last_purchase ? fmtDate(s.last_purchase) : "—"],
              ["Avg Gap", s.avg_gap_days != null ? `${s.avg_gap_days} days` : "—"]].map(([l, v]) => (
              <Card key={l} className="p-4 border-slate-200 rounded-xl shadow-none">
                <div className="font-display text-lg font-semibold text-slate-900">{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{l}</div>
              </Card>
            ))}
          </div>
          <Card className="p-5 border-slate-200 rounded-xl shadow-none">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Monthly Buying (value ₹)</div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <RTooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="value" fill="#3B82F6" radius={[6, 6, 0, 0]} maxBarSize={44} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="border-slate-200 rounded-xl shadow-none overflow-x-auto">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 px-5 pt-4">What they buy</div>
            <table className="w-full min-w-[720px]">
              <thead><tr><th className={th}>Product</th><th className={th}>Top Size</th><th className={`${th} text-right`}>Total Qty</th>
                <th className={`${th} text-right`}>Avg Rate</th><th className={`${th} text-right`}>Total Value</th><th className={th}>Times</th><th className={th}>Last Bought</th></tr></thead>
              <tbody className="divide-y divide-slate-50" data-testid="trend-products">
                {data.top_products.map((p) => (
                  <tr key={p.product} className="hover:bg-slate-50/60">
                    <td className={`${td} font-medium`}>{p.product}</td>
                    <td className={td}>{p.top_size}</td>
                    <td className={`${td} text-right tabular-nums`}>{p.qty}</td>
                    <td className={`${td} text-right tabular-nums`}>₹{p.avg_rate}</td>
                    <td className={`${td} text-right tabular-nums font-semibold`}>{inr(p.value)}</td>
                    <td className={td}>{p.times}×</td>
                    <td className={td}>{p.last_date ? fmtDate(p.last_date) : "—"}</td>
                  </tr>
                ))}
                {data.top_products.length === 0 && <tr><td colSpan={7} className="text-center text-sm text-slate-400 py-8">No purchase data yet.</td></tr>}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

export function SizeStockTab() {
  return <StockTab />;
}

export function BuyingTrendsTab() {
  return <TrendsTab />;
}

export default function SizeIntelligence() {
  return (
    <div className="space-y-6" data-testid="size-intel-page">
      <PageHeader title="Size Intelligence" subtitle="Enter a size — see every quote, order & invoice ever made for it" />
      <SizeFinder />
    </div>
  );
}
